import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { PenLine, Loader2 } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
export default function AuthCallback() {
  const location = useLocation();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      setError("Missing session id");
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        // Clear the hash so future reloads don't re-run this.
        window.history.replaceState(null, "", window.location.pathname);
        await refresh?.(data.user);
        toast.success(`Welcome, ${data.user.name.split(" ")[0]}`);
        nav("/", { replace: true, state: { user: data.user } });
      } catch (e) {
        console.error("Google session exchange failed", e);
        setError(e?.response?.data?.detail || "Sign-in failed. Please try again.");
      }
    })();
  }, [location.hash, nav, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <div className="max-w-sm text-center" data-testid="auth-callback">
        <div className="flex items-center justify-center gap-2 mb-4">
          <PenLine className="w-5 h-5" style={{ color: "var(--primary)" }} />
          <span className="font-serif-display text-2xl">Scriptora</span>
        </div>
        {error ? (
          <>
            <div className="mono-label mb-2">Sign-in error</div>
            <p className="text-sm mb-4" style={{ color: "var(--ink-2)" }}>{error}</p>
            <button
              onClick={() => nav("/auth", { replace: true })}
              className="btn-primary px-4 py-2 rounded-md text-sm"
              data-testid="auth-callback-back"
            >
              Back to sign in
            </button>
          </>
        ) : (
          <div className="flex items-center justify-center gap-2 mono-label">
            <Loader2 className="w-4 h-4 animate-spin" /> Signing you in…
          </div>
        )}
      </div>
    </div>
  );
}
